/**
 * MEIA Studio — Context Builder Type Definitions
 * Schema Version: v4.3.5 | Context Builder Version: 1.4.0
 *
 * v4.2 (Engagement Model & Kill-Chain Contest layer) and v4.3
 * (component-granularity + engagement-status) additions are appended
 * at the bottom of this file. v4.3.3 adds scoring-inert options-inbox
 * governance fields — all types remain OPTIONAL on the raw data
 * structures so a v4.1.0/v4.2.0/v4.3.0 study file loads into v4.3.5
 * without requiring any new field. See the spec at
 *   docs / MEIA_v4.2_Engagement_Model_Spec.md
 * and the original v4.1.0 doc-block below for baseline semantics.
 *
 * The Context Builder is the orchestration layer that transforms raw
 * schema instance data (JSON) into the typed contexts consumed by the
 * five analytical engines:
 *   1. Scoring Engine      → ScoringContext
 *   2. Transfer Function   → TransferFunctionContext
 *   3. Sensitivity Analysis → SensitivityContext
 *   4. Cost-Effectiveness  → CostEffectivenessContext
 *   5. Explainability      → (uses ScoringContext + ScoringOutputs)
 *
 * RAW vs TYPED:
 *   - Raw: JSON from schema instance files (_data arrays). Strings for
 *     threshold/objective, epoch-keyed maps, nullable/missing fields.
 *   - Typed: Parsed, validated, numeric. Ready for engine consumption.
 *
 * The Context Builder handles:
 *   1. Epoch resolution — resolving techSelections per epoch with fallback
 *   2. MOP threshold/objective string parsing → numeric
 *   3. Technology snapshot resolution — enriching inline task techs from `Technology`-sourced MEIA snapshot rows
 *   4. Parameter override merging — base defaults + run case overrides
 *   5. Interface degradation assembly
 *   6. Cost profile resolution for cost-effectiveness
 *   7. Validation of structural integrity before engine execution
 *
 * DOCTRINE ALIGNMENT:
 *   - OODA: Orient phase — assembling the common operating picture
 *   - Pure function: RawStudyData + RunCaseConfig → EngineContexts
 *   - Deterministic, no side effects
 */
export { ScoringBand } from './scoring.types';
// ─────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────
/** v4.2 — Engagement Model version, written into EngagementOutput.provenance */
export const ENGAGEMENT_MODEL_VERSION = '1.0.0';
/** v4.2 — Model registry ID for the MEIA Studio Engagement Model (MDL-002) */
export const ENGAGEMENT_MODEL_REGISTRY_ID = 'MDL-002';
/** Valid epoch values from schema */
export const Epoch = {
    BEYOND: 'BEYOND',
    FYDP: 'FYDP',
    NEAR_TERM: 'NEAR_TERM',
};
/** Epoch fallback order: beyond → fydp → near-term */
export const EPOCH_FALLBACK_ORDER = {
    [Epoch.NEAR_TERM]: [],
    [Epoch.FYDP]: [Epoch.NEAR_TERM],
    [Epoch.BEYOND]: [Epoch.FYDP, Epoch.NEAR_TERM],
};
// ─────────────────────────────────────────────────────────────
// v4.3.5 — ACR Register (Assumptions, Constraints, Risks)
// ─────────────────────────────────────────────────────────────
/**
 * v4.3.5 — ACR category. Governs which fields are required:
 * - Risk entries require probability + severity (BV-046, ERROR)
 * - Risk entries with status='open' and empty mitigationApproach
 *   block G4 sign-off (BV-048, ERROR)
 * Assumption / Constraint entries are silent under BV-046/048.
 */
export var AcrCategory;
(function (AcrCategory) {
    AcrCategory["ASSUMPTION"] = "ASSUMPTION";
    AcrCategory["CONSTRAINT"] = "CONSTRAINT";
    AcrCategory["RISK"] = "RISK";
})(AcrCategory || (AcrCategory = {}));
/**
 * v4.3.5 — ACR lifecycle state. Round 2 Gap #19 locked 2026-04-19.
 */
export var AcrStatus;
(function (AcrStatus) {
    AcrStatus["ACCEPTED"] = "ACCEPTED";
    AcrStatus["CLOSED"] = "CLOSED";
    AcrStatus["MITIGATED"] = "MITIGATED";
    AcrStatus["OPEN"] = "OPEN";
})(AcrStatus || (AcrStatus = {}));
/**
 * Categorical probability band. Present for Risk entries only (BV-046);
 * null/absent on Assumption/Constraint entries. Five-band qualitative scale
 * (VERY LOW → VERY HIGH) backing the DoD 5×4 risk matrix — expanded from the
 * original three bands per AZ-454 D9 to match the MEIA prototype's probability
 * axis. Pair with the 4-band {@link AcrSeverity}.
 */
export var AcrProbability;
(function (AcrProbability) {
    AcrProbability["HIGH"] = "HIGH";
    AcrProbability["LOW"] = "LOW";
    AcrProbability["MEDIUM"] = "MEDIUM";
    AcrProbability["VERY_HIGH"] = "VERY_HIGH";
    AcrProbability["VERY_LOW"] = "VERY_LOW";
})(AcrProbability || (AcrProbability = {}));
/**
 * v4.3.5 — categorical severity band. Present for Risk entries
 * only (BV-046). null/absent on Assumption/Constraint entries.
 * Four-band qualitative scale per MEIA — distinct from the 5-band
 * RiskImpact taxonomy used by StudyRisk.
 */
export var AcrSeverity;
(function (AcrSeverity) {
    AcrSeverity["CRITICAL"] = "CRITICAL";
    AcrSeverity["HIGH"] = "HIGH";
    AcrSeverity["LOW"] = "LOW";
    AcrSeverity["MEDIUM"] = "MEDIUM";
})(AcrSeverity || (AcrSeverity = {}));
/**
 * v4.3 classification enum. SCREAMING_SNAKE values per the Azymmetric
 * Postgres-enum convention. Ordering for monotonicity (BV-024):
 * UNCLASSIFIED < CUI < SECRET < TOP_SECRET.
 */
export const ClassificationLevel = {
    CUI: 'CUI',
    SECRET: 'SECRET',
    TOP_SECRET: 'TOP_SECRET',
    UNCLASSIFIED: 'UNCLASSIFIED',
};
/** v4.3 component-type enum (SME-locked 2026-04-17, spec §3.1). */
export const ComponentType = {
    C2: 'C2',
    DATALINK: 'DATALINK',
    EW: 'EW',
    LAUNCHER: 'LAUNCHER',
    NAVIGATION: 'NAVIGATION',
    OTHER: 'OTHER',
    POWER: 'POWER',
    PROCESSOR: 'PROCESSOR',
    SENSOR: 'SENSOR',
};
/** v4.3.1a engagement planning-lifecycle enum. */
export const EngagementStatus = {
    APPROVED: 'APPROVED',
    DRAFT: 'DRAFT',
    PROPOSED: 'PROPOSED',
    REJECTED: 'REJECTED',
    RETIRED: 'RETIRED',
};
/** Default composite weights shipped in v4.2 (§4.2). */
export const DEFAULT_COMPOSITE_WEIGHTS = {
    range: 0.3,
    timing: 0.3,
    probability: 0.4,
};
/** Shipped default contest-config values. */
export const DEFAULT_CONTEST_CONFIG = {
    preferredContestType: 'Auto',
    rangeMarginPct: 10,
    timingSafetyMarginSec: 2,
    compositeWeights: DEFAULT_COMPOSITE_WEIGHTS,
    mismatchWarningTolerance: 0.15,
    suppressionBandComputationMode: 'Auto',
};
